import type { Cliente, Compromisso } from '../db/tipos'
import { dataISOLocal } from '../pedidos/precos'
import { avisoValido } from './aviso'

// ▶ As regras dos compromissos (spec 2026-10-02, ciclo 2A). As telas so desenham.
// Tudo compara STRINGS locais ('AAAA-MM-DD', 'HH:MM'): nada de ISO em UTC
// (licao bug-visita-fuso-utc).

export type RascunhoCompromisso = {
  data: string
  hora: string
  assunto: string
  cliente_id: string | null
  // Aviso com antecedencia (0034). null = sem aviso.
  aviso_minutos: number | null
}

const vivo = (c: Compromisso) => c.apagado_em === null

export function horaLocal(agora: Date): string {
  return `${String(agora.getHours()).padStart(2, '0')}:${String(agora.getMinutes()).padStart(2, '0')}`
}

export function ehAtrasado(c: Compromisso, agora: Date): boolean {
  if (c.situacao !== 'pendente') return false
  const hoje = dataISOLocal(agora)
  return c.data < hoje || (c.data === hoje && c.hora < horaLocal(agora))
}

export function corDoDia(lista: Compromisso[], agora: Date): 'vermelha' | 'verde' | 'cinza' | null {
  const vivos = lista.filter(vivo)
  if (vivos.length === 0) return null
  if (vivos.some((c) => ehAtrasado(c, agora))) return 'vermelha'
  if (vivos.every((c) => c.situacao === 'feito')) return 'verde'
  return 'cinza'
}

export function compromissosDoDia(lista: Compromisso[], data: string): Compromisso[] {
  return lista
    .filter((c) => vivo(c) && c.data === data)
    .sort((a, b) => a.hora.localeCompare(b.hora) || a.criado_em.localeCompare(b.criado_em))
}

// Teste dela 04/10, item 7: na lista do dia o excluido nao some, fica cinza.
// So a tela Meus compromissos usa; Inicio, bolinha e alerta seguem so os vivos.
export function compromissosDoDiaComExcluidos(lista: Compromisso[], data: string): Compromisso[] {
  return lista
    .filter((c) => c.data === data)
    .sort((a, b) => a.hora.localeCompare(b.hora) || a.criado_em.localeCompare(b.criado_em))
}

// Teste dela 04/10, item 6: o que a janela vermelha lista, mais antigo primeiro.
export function atrasados(lista: Compromisso[], agora: Date): Compromisso[] {
  return lista
    .filter((c) => vivo(c) && ehAtrasado(c, agora))
    .sort((a, b) => a.data.localeCompare(b.data) || a.hora.localeCompare(b.hora))
}

export const INTERVALO_DO_ALERTA_MS = 60 * 60_000

// Ao abrir o app (nunca mostrado) e depois de hora em hora.
export function horaDeAlertar(ultimoMs: number | null, agoraMs: number): boolean {
  return ultimoMs === null || agoraMs - ultimoMs >= INTERVALO_DO_ALERTA_MS
}

// Desenho dela 04/10 da janela vermelha: ha quanto tempo esta atrasado.
export function textoDoAtraso(c: Compromisso, agora: Date): string {
  const [a, m, d] = c.data.split('-').map(Number)
  const [h, min] = c.hora.split(':').map(Number)
  const minutos = Math.max(1, Math.floor((agora.getTime() - new Date(a, m - 1, d, h, min).getTime()) / 60_000))
  if (minutos < 60) return `Atrasado há ${minutos} min`
  const horas = Math.floor(minutos / 60)
  if (horas < 24) return `Atrasado há ${horas} ${horas === 1 ? 'hora' : 'horas'}`
  const dias = Math.floor(horas / 24)
  return `Atrasado há ${dias} ${dias === 1 ? 'dia' : 'dias'}`
}

export function diaDaSemanaCurto(data: string): string {
  const [a, m, d] = data.split('-').map(Number)
  return ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'][new Date(a, m - 1, d).getDay()]
}

// Semanas de domingo a sabado; null nos buracos antes do dia 1 e depois do ultimo.
export function semanasDoMes(ano: number, mes: number): (string | null)[][] {
  const ultimo = new Date(ano, mes + 1, 0).getDate()
  const celulas: (string | null)[] = Array(new Date(ano, mes, 1).getDay()).fill(null)
  for (let dia = 1; dia <= ultimo; dia++) celulas.push(dataISOLocal(new Date(ano, mes, dia)))
  while (celulas.length % 7 !== 0) celulas.push(null)
  const semanas: (string | null)[][] = []
  for (let i = 0; i < celulas.length; i += 7) semanas.push(celulas.slice(i, i + 7))
  return semanas
}

export function textoDosCompromissos(n: number): string {
  if (n === 0) return 'Nenhum compromisso'
  return `${n} ${n === 1 ? 'compromisso' : 'compromissos'}`
}

const DIAS_CURTOS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']
const MESES_CURTOS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']

export function dataDoTopo(d: Date): string {
  return `${DIAS_CURTOS[d.getDay()]}, ${d.getDate()} de ${MESES_CURTOS[d.getMonth()]}`
}

export const LIMITE_ASSUNTO = 120

export function validarCompromisso(r: RascunhoCompromisso): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(r.data)) return 'Escolha a data.'
  // Alguns seletores (iOS/desktop) mandam 'HH:MM:SS': vale so 'HH:MM'.
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(r.hora.slice(0, 5))) return 'Escolha a hora.'
  const assunto = r.assunto.trim()
  if (assunto === '') return 'Escreva o assunto.'
  if (assunto.length > LIMITE_ASSUNTO) return `O assunto pode ter até ${LIMITE_ASSUNTO} letras.`
  if (!avisoValido(r.aviso_minutos)) return 'Escolha o aviso.'
  return null
}

// Fantasia, senao razao social. Cliente sumido ou apagado devolve null: o
// compromisso continua na tela, so sem o nome (nunca quebra).
export function nomeDoCliente(clientes: Cliente[], id: string | null): string | null {
  if (id === null) return null
  const c = clientes.find((x) => x.id === id && x.apagado_em === null)
  return c ? c.nome_fantasia || c.razao_social : null
}
